import { FC, useState, useMemo } from 'react';
import { useGetTechMapSymbolCatalogQuery } from '@/shared/state/api/swagger';
import { Select } from 'antd';
import styles from '../custom-select/custom-select.module.scss';
import clsx from 'clsx';

interface ProductGroupSelectProps {
	value?: string;
	onChange?: (value: string) => void;
	size?: 'large' | 'middle' | 'small';
	defaultValue?: string;
	[key: string]: any;
}

export const ProductGroupSelect: FC<ProductGroupSelectProps> = ({
	value,
	onChange,
	size = 'large',
	defaultValue,
	...props
}) => {
	const [searchValue, setSearchValue] = useState('');

	const { options: baseOptions, isFetching } = useGetTechMapSymbolCatalogQuery(undefined, {
		selectFromResult: (result) => ({
			...result,
			options:
				result?.data
					?.filter((item) => item.symbol)
					.map((item) => ({
						value: item.symbol,
						label: item.symbol,
					})) ?? [],
		}),
	});

	const options = useMemo(() => {
		const allOptions = [...baseOptions];
		const valueToAdd = searchValue || value;

		if (valueToAdd && !baseOptions.some((opt) => opt.value === valueToAdd)) {
			allOptions.unshift({
				value: valueToAdd,
				label: valueToAdd,
			});
		}

		return allOptions;
	}, [baseOptions, searchValue, value]);

	const handleSearch = (val: string) => {
		if (val.length <= 2) {
			setSearchValue(val);
		}
	};

	const handleChange = (val: string) => {
		setSearchValue('');
		onChange?.(val || ('' as any));
	};

	const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		const input = e.currentTarget;
		const currentValue = input.value || '';

		if (currentValue.length >= 2) {
			e.preventDefault();
		}
	};

	return (
		<Select
			{...props}
			value={value}
			onChange={handleChange}
			onSearch={handleSearch}
			onInputKeyDown={handleInputKeyDown}
			defaultValue={defaultValue}
			options={options}
			loading={isFetching}
			showSearch
			allowClear
			size={size}
			className={clsx(styles['select'], size === 'large' && styles['select-large'])}
			dropdownStyle={{ zIndex: 1000000 }}
			optionFilterProp="children"
			filterOption={(input, option) => {
				const label = String(option?.label ?? '');
				return label.toLowerCase().includes(input.toLowerCase());
			}}
		/>
	);
};
